import { useNavigate, useLocation } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { ScoreDialog } from './ScoreDialog';
import { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Download, CheckCircle, XCircle, Minus, Edit } from 'lucide-react';
import { CreateCompetition, getClimbByCompetition, getCompetitionById, UpdateClimb } from '../services/apiServices';

export const Results = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [competition, setCompetition] = useState(null);
  const [climbData, setClimbData] = useState([]);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [selectedBoulder, setSelectedBoulder] = useState(null);
  const [selectedClimber, setSelectedClimber] = useState(null);


  const formatClimbResult = useCallback((score) => {
    if (!score) return null;

    if (score.topReached) {
      if (score.zoneReached) {
        return `${score.zoneAttempt}-Z, ${score.topAttempt}-T`;
      }
      return `${score.topAttempt}-T`;
    } else if (score.zoneReached) {
      return `${score.zoneAttempt}-Z`;
    } else if (score.attempts > 0) {
      return `${score.attempts}-A`;
    }
    return null;
  }, []);

  useEffect(() => {
    async function fetchCompetition() {
      try {
        const response = await getCompetitionById(location.state?._id);
        if (!response || response === "false") {
          return;
        } else {
          const competitionData = await response.json();
          console.log(competitionData, 'competition data');
          setCompetition(competitionData);
        }
      } catch (error) {
        console.error("Error fetching competition:", error);
      }
    }
    if (location.state?._id) {
      fetchCompetition();
    }
  }, [location.state]);

  useEffect(() => {
    async function fetchClimb() {
      console.log("Chinna123")
      try {
        const response = await getClimbByCompetition(location.state?._id);
        if (!response || response === "false") {
          return;
        } else {
          const climbData = await response.json();
          console.log(climbData, 'climb data');
          setClimbData(climbData);
        }
      } catch (error) {
        console.error("Error fetching climb data:", error);
      }
    }
    if (location.state?._id) {
      fetchClimb();
    }
  }, [location.state]);

  // Calculate score for a single event
  const calculateScore = (event) => {
    if (!event) return 0;

    let zonePoints = 0;
    let topPoints = 0;
    const Zone = competition?.zonepoints || 10;

    const Top = competition?.toppoints || 25
    const penalty = competition?.penalty || 0.1


    // Zone points calculation: 10 points minus 0.1 for each attempt beyond the first
    if (event.zoneReached && event.zoneAttempt) {
      zonePoints = Zone - (event.zoneAttempt - 1) * penalty;
    }

    // Top points calculation: 25 points minus 0.1 for each attempt beyond the first
    if (event.topReached && event.topAttempt) {
      topPoints = Top - (event.topAttempt - 1) * penalty;
    }

    // Return the highest score achieved (either zone or top)
    return Math.max(zonePoints, topPoints);
  };
  // Get all climbers from competition data (not just from climbData)
  const getAllClimbers = () => {
    if (!competition || !competition.competitors) return [];

    // First, get climbers from competition data
    const competitionClimbers = competition.competitors.map(climberId => {
      // Try to find this climber in climbData to get full info
      const climbInfo = climbData.find(item => item.climberID?._id === climberId);
      if (climbInfo && climbInfo.climberID) {
        return climbInfo.climberID;
      }
      return null;
    }).filter(Boolean);

    // Also add climbers from climbData that might not be in competition.competitors
    const climbDataClimbers = [];
    climbData.forEach(item => {
      if (item.climberID && item.climberID._id &&
        !competitionClimbers.find(c => c._id === item.climberID._id)) {
        climbDataClimbers.push(item.climberID);
      }
    });

    return [...competitionClimbers, ...climbDataClimbers];
  };

  // Get boulders from competition data with proper names
  const getBoulders = () => {
    if (!competition || !competition.boulders) return [];
    return competition.boulders.map(boulder => ({
      id: boulder._id,
      name: boulder.name
    }));
  };

  // Get score for a specific climber and boulder
  const getScoreForClimberBoulder = (climberId, boulderId) => {
    return climbData.find(
      item => item.climberID?._id === climberId && item.boulderID === boulderId
    );
  };

  // Calculate total score for a climber across all boulders
  const getTotalScoreForClimber = (climberId) => {
    const boulders = getBoulders();
    let totalScore = 0;

    boulders.forEach(boulder => {
      const score = getScoreForClimberBoulder(climberId, boulder.id);
      totalScore += calculateScore(score);
    });

    return totalScore;
  };

  // Sort climbers by total score (highest first)
  const getSortedClimbers = () => {
    const climbers = getAllClimbers();
    return climbers.sort((a, b) => {
      const scoreA = getTotalScoreForClimber(a._id);
      const scoreB = getTotalScoreForClimber(b._id);
      return scoreB - scoreA;
    });
  };

  // Calculate summary statistics
  const calculateSummary = () => {
    const totalScores = climbData.length;
    const tops = climbData.filter(item => item.topReached).length;
    const zones = climbData.filter(item => item.zoneReached).length;
    const uniqueClimbers = getAllClimbers().length;
    const uniqueBoulders = getBoulders().length;
    const totalPossible = uniqueClimbers * uniqueBoulders;
    const completion = totalPossible > 0 ? Math.round((totalScores / totalPossible) * 100) : 0;

    return {
      totalScores,
      tops,
      zones,
      completion
    };
  };

  const handleOpenScoreDialog = (boulder, climber) => {
    setSelectedBoulder(boulder);
    setSelectedClimber(climber);
    setShowScoreDialog(true);
  };

  const getExistingScore = () => {
    if (!selectedBoulder || !selectedClimber) return undefined;
    return getScoreForClimberBoulder(selectedClimber._id, selectedBoulder.id);
  };
  const handleExportCSV = () => {
    const climbers = getSortedClimbers();
    const boulders = getBoulders();

    // Build CSV header
    const header = [
      'Rank',
      'Bib',
      'Name',
      ...boulders.map(b => b.name),
      'Total Score',
      'Completion %'
    ].join(',');

    // Build CSV rows with detailed information in single column
    const rows = climbers.map((climber, index) => {
      const boulderResults = boulders.map(boulder => {
        const score = getScoreForClimberBoulder(climber._id, boulder.id);

        if (!score) {
          return 'No attempts'; // No attempts
        }

        let result = '';
        const attempts = score.attempts || 0;
        const boulderScore = calculateScore(score);

        if (score.topReached) {
          const topAttempt = score.topAttempt || '0';
          const zoneAttempt = score.zoneAttempt || '0';
          result = `T${topAttempt} (Z${zoneAttempt}) - ${attempts} att - Score: ${boulderScore}`;
        } else if (score.zoneReached) {
          const zoneAttempt = score.zoneAttempt || '?';
          result = `Z${zoneAttempt} - ${attempts} att - Score: ${boulderScore}`;
        } else if (score.attempts > 0) {
          result = `A${attempts} - Score: ${boulderScore}`;
        }

        return result;
      });

      const totalScore = getTotalScoreForClimber(climber._id);

      // Calculate completion percentage
      const completedBoulders = boulders.filter(boulder => {
        const score = getScoreForClimberBoulder(climber._id, boulder.id);
        return score && (score.topReached || score.zoneReached || score.attempts > 0);
      }).length;

      const completionPercentage = boulders.length > 0
        ? Math.round((completedBoulders / boulders.length) * 100)
        : 0;

      return [
        index + 1, // Rank
        climber.bib || '',
        climber.name || '',
        ...boulderResults,
        totalScore,
        completionPercentage
      ].join(',');
    });

    const csv = [header, ...rows].join('\n');

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${competition.name.replace(/[^a-zA-Z0-9]/g, '_')}_results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const handleSaveScore = async (scoreData) => {
    try {
      console.log('Saving score data:', scoreData);

      // If scoreData has _id, it's an update, otherwise it's a create
      const climbId = scoreData._id; // This will be undefined for new climbs

      const response = await UpdateClimb(climbId, scoreData);

      if (response && response !== "false") {
        const result = await response.json();
        console.log('Save successful:', result);

        // Refresh the climb data to get the updated results
        const updatedClimbResponse = await getClimbByCompetition(location.state?._id);
        if (updatedClimbResponse && updatedClimbResponse !== "false") {
          const updatedClimbData = await updatedClimbResponse.json();
          setClimbData(updatedClimbData);
        }
      } else {
        console.error('Failed to save score');
      }
    } catch (error) {
      console.error('Error saving score:', error);
    }
  };
  const handleAnnouncement = async (isFinal) => {
    if (competition?._id) {
      const competitionData = {
        _id: competition._id,
        finalannocement: isFinal
      };
      const response = await CreateCompetition(competition?._id, competitionData);
      const responded = await response.json();
      setCompetition(responded);
    }
  }

  if (!competition) {
    return (
      <div className="min-h-screen p-4 md:p-6 max-w-4xl mx-auto">
        <Alert>
          <AlertDescription>
            Loading competition data...
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const climbers = getSortedClimbers();
  const boulders = getBoulders();
  const summary = calculateSummary();
  console.log(summary, 'summary');

  return (
    <div className="min-h-screen p-4 md:p-6 max-w-6xl mx-auto">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="size-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl">Results</h1>
            <p className="text-gray-600">{competition.name} • {competition.category}</p>
          </div>
          <Button onClick={handleExportCSV} className='flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium'>
            <Download className="size-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Competition Summary</CardTitle>
            <CardDescription>
              {climbers.length} competitors × {boulders.length} boulders
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Scores</p>
              <p className="text-2xl">{summary.totalScores}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Tops</p>
              <p className="text-2xl">{summary.tops}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Zones</p>
              <p className="text-2xl">{summary.zones}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Completion</p>
              <p className="text-2xl">{summary.completion}%</p>
            </div>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card>
          <CardHeader>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div>
                  <CardTitle>Score Matrix</CardTitle>
                  <CardDescription>Detailed results for all competitors</CardDescription>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      // If finalannocement is undefined, null, or 0, allow provisional announcement
                      if (!competition?.finalannocement || competition.finalannocement === 0) {
                        handleAnnouncement(1);
                      }
                    }}
                    disabled={competition?.finalannocement === 1 || competition?.finalannocement === 2}
                    className={`
      px-4 py-2 text-sm font-medium border rounded-md transition-colors
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500
      ${(competition?.finalannocement === 1 || competition?.finalannocement === 2)
                        ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'
                        : 'bg-yellow-600 text-white border-yellow-600 hover:bg-yellow-700'
                      }
    `}
                  >
                    {competition?.finalannocement === 1 ? 'Provisional Published' : 'Provisional Announcement'}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAnnouncement(2)}
                    disabled={competition?.finalannocement === 2}
                    className={`
      px-4 py-2 text-sm font-medium border rounded-md transition-colors
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
      ${competition?.finalannocement === 2
                        ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed'
                        : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                      }
    `}
                  >
                    {competition?.finalannocement === 2 ? 'Final Published' : 'Final Announcement'}
                  </button>
                </div>
              </div>
            </CardHeader>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Bib</TableHead>
                  <TableHead className="min-w-[150px]">Name</TableHead>
                  {boulders.map(boulder => (
                    <TableHead key={boulder.id} className="text-center min-w-[120px]">
                      {boulder.name}
                    </TableHead>
                  ))}
                  <TableHead className="text-center min-w-[100px] bg-gray-100">
                    Total Score
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {climbers.map(climber => {
                  const totalScore = getTotalScoreForClimber(climber._id);

                  return (
                    <TableRow key={climber._id}>
                      <TableCell className="text-center bg-blue-50">
                        {climber.bib}
                      </TableCell>
                      <TableCell>{climber.name}</TableCell>
                      {boulders.map(boulder => {
                        const score = getScoreForClimberBoulder(climber._id, boulder.id);
                        const boulderScore = calculateScore(score);
                        const climbResult = formatClimbResult(score)
                        return (
                          <TableCell
                            key={boulder.id}
                            className="text-center cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => handleOpenScoreDialog(boulder, climber)}
                          >
                            {score ? (
                              <div className="space-y-1 py-2">
                                <div className="flex items-center justify-center gap-2">
                                  {/* {score.topReached ? (
                                    <CheckCircle className="size-4 text-green-600" />
                                  ) : score.zoneReached ? (
                                    <CheckCircle className="size-4 text-yellow-600" />
                                  ) : (
                                    <XCircle className="size-4 text-gray-400" />
                                  )} */}
                                  {/* <span className="text-sm font-medium">
                                    {score.topReached ? (score.zoneReached ? `${score.zoneAttempt}-Z, ${score.topAttempt}-T` : `${score.topAttempt}-T`) :
                                      score.zoneReached ? `${score.zoneAttempt}-Z` :
                                        `${score.attempts}-A`}
                                  </span> */}
                                  <span className="text-sm font-semibold text-slate-800 text-blue-600">
                                    {score.attempts > 0 ? <span>{climbResult}: <span className='text-green-600'>{boulderScore.toFixed(1)}</span></span> : <span
                                      className='text-blue-600'
                                    >--</span>}
                                  </span>
                                </div>
                                {score.attempts > 0 &&
                                  <div className="text-xs text-gray-600">
                                    {score.attempts} {score.attempts === 1 ? 'att' : 'atts'}
                                  </div>}
                                {/* <div className="text-xs font-semibold text-blue-600">
                                  Score: {boulderScore}
                                </div> */}

                              </div>
                            ) : (
                              <div className="py-2 space-y-1">
                                <Minus className="size-4 text-gray-300 mx-auto" />
                                <div className="text-xs text-blue-600">
                                  Click to add
                                </div>
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center bg-gray-50 font-bold text-lg">
                        {totalScore}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Score Dialog */}
      {selectedBoulder && selectedClimber && (
        <ScoreDialog
          open={showScoreDialog}
          onOpenChange={setShowScoreDialog}
          boulder={selectedBoulder}
          climber={selectedClimber}
          existingScore={getExistingScore()}
          onSave={handleSaveScore}
          competitionId={location.state?._id} // Pass competition ID
        />
      )}
    </div>
  );
};